import { forwardRef, useState, useEffect, useCallback } from 'react';
import { Button, Flex, Heading, Text } from '@radix-ui/themes';
import axios from 'axios';
import config from '../../config';
import './Home.css';
import SEO from '../SEO/SEO';
import { localBusinessSchema, websiteSchema } from '../../utils/structuredData';
import { analytics } from '../../utils/analytics';
import { useSiteConfig } from '../../utils/siteConfig';

// Slide images are served from DigitalOcean Spaces — the backend lists
// the `carousel/` prefix in the bucket and returns the file URLs in
// filename order. Drop new images in the bucket (with a numeric prefix
// like `01-...`, `02-...` for ordering) and they appear here on the next
// cache miss (~5 min) or after POSTing to /content-images/refresh.
interface Slide {
  src: string;
  alt: string;
  className: string;
}

// Visual offsets cycled through the carousel for the slight stagger effect.
const OFFSET_CLASSES = ['offset-one', 'offset-two', 'offset-three', 'offset-five'];

const INTERVAL = 4000;

const Home = forwardRef<HTMLDivElement>((_, ref) => {
  const siteConfig = useSiteConfig();
  const heroHeadline = siteConfig.get('homeHeroHeadline', "Grand Junction's Pub Trivia");
  const heroSubtext = siteConfig.get(
    'homeHeroSubtext',
    'Weekly trivia nights at local bars & breweries across the Grand Valley',
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [slides, setSlides] = useState<Slide[]>([]);

  useEffect(() => {
    const fetchCarouselImages = async () => {
      try {
        const res = await axios.get(`${config.apiBaseUrl}/content-images?category=carousel`);
        const images = res.data?.data?.images;
        if (images && images.length > 0) {
          setSlides(
            images.map((img: { url: string; alt: string }, i: number) => ({
              src: img.url,
              alt: img.alt,
              className: OFFSET_CLASSES[i % OFFSET_CLASSES.length],
            }))
          );
        }
      } catch {
        // Bucket unreachable — leave slides empty. The carousel JSX
        // guards against zero-length slides below.
      }
    };
    fetchCarouselImages();
  }, []);

  const next = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    const timer = setInterval(next, INTERVAL);
    return () => clearInterval(timer);
  }, [next]);

  const scrollToSchedule = () => {
    analytics.heroCtaClicked('upcoming_events');
    const schedule = document.querySelector('.upcoming-dates-container');
    if (schedule) {
      schedule.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div ref={ref}>
      <SEO
        title="West Slope Quiz | Grand Junction Pub Trivia Nights"
        description="West Slope Quiz hosts weekly pub trivia nights at bars and restaurants across Grand Junction and the Grand Valley. Join us weekly or book a private event — corporate, birthday, or fundraiser."
        canonical="/"
        structuredData={[
          localBusinessSchema(siteConfig.get('brandName', 'West Slope Quiz')),
          websiteSchema(siteConfig.get('brandName', 'West Slope Quiz')),
        ]}
      />
      <div className="carouselContainer">
        <div className="hero-overlay">
          <div className="hero-content">
            <Heading as="h1" className="hero-headline">{heroHeadline}</Heading>
            <Text as="p" className="hero-subheadline">{heroSubtext}</Text>
            <Flex wrap="wrap" gap="3" justify="center" className="hero-ctas">
              <Button size="3" onClick={scrollToSchedule}>
                See Upcoming Events
              </Button>
              <Button size="3" variant="outline" className="hero-btn--secondary" asChild>
                <a href="/workwithus" onClick={() => analytics.heroCtaClicked('book_private')}>
                  Book a Private Event
                </a>
              </Button>
            </Flex>
          </div>
        </div>

        <div className="carousel" aria-roledescription="carousel" aria-label="Event photos">
          {slides.map((slide, i) => (
            <div
              key={`${slide.className}-${i}`}
              className={`carousel-slide ${i === activeIndex ? 'carousel-slide--active' : ''}`}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${slides.length}`}
              aria-hidden={i !== activeIndex}
            >
              <img
                className={`home ${slide.className}`}
                src={slide.src}
                alt={slide.alt}
                loading={i === 0 ? 'eager' : 'lazy'}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

Home.displayName = 'Home';

export default Home;
